import React, { useState } from 'react'
import { useTheme } from '../hooks/useTheme'
import Footer from '../components/common/Footer'
import SimpleHeader from '../components/common/SimpleHeader'
import toast from 'react-hot-toast'

const ContactPage: React.FC = () => {
  const { colors } = useTheme()
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    asunto: '',
    mensaje: ''
  })
  const [isLoading, setIsLoading] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Validación básica
    if (!formData.nombre || !formData.email || !formData.mensaje) {
      toast.error('Por favor completa los campos requeridos')
      return
    }

    setIsLoading(true)
    try {
      // Enviar email directamente a contacto@playanik.cl
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/emails/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono,
          asunto: formData.asunto,
          mensaje: formData.mensaje,
          to_email: 'contacto@playanik.cl'
        })
      })

      if (!response.ok) {
        throw new Error('Error al enviar el mensaje')
      }

      toast.success('¡Mensaje enviado correctamente! Nos pondremos en contacto pronto.')
      setFormData({
        nombre: '',
        email: '',
        telefono: '',
        asunto: '',
        mensaje: ''
      })
    } catch (error) {
      console.error('Error:', error)
      toast.error('Error al enviar el mensaje. Por favor intenta de nuevo.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <SimpleHeader
        title="Contacto"
        subtitle="¿Tienes preguntas? Estamos aquí para ayudarte"
      />

      {/* Content */}
      <div className="flex-grow max-w-6xl mx-auto w-full px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Información de contacto */}
          <div>
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Información de Contacto</h2>

            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                <a
                  href="mailto:contacto@playanik.cl"
                  className="text-lg hover:opacity-80 transition-opacity"
                  style={{ color: colors.primary }}
                >
                  contacto@playanik.cl
                </a>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Ubicación</h3>
                <p className="text-gray-600">Ruta V225, Kilometro 8, Puerto Varas, Los Lagos, Chile</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Horario de Atención</h3>
                <p className="text-gray-600">Lunes a Domingo</p>
                <p className="text-gray-600">9:00 - 20:00 horas</p>
              </div>

              <div className="pt-4">
                <p className="text-gray-600 leading-relaxed">
                  Si tienes alguna pregunta sobre nuestras reservas, tarifas o necesitas asistencia especial,
                  no dudes en ponerte en contacto con nosotros. Nos encantaría ayudarte a planificar tu próxima
                  aventura en el camping.
                </p>
              </div>
            </div>
          </div>

          {/* Formulario */}
          <div>
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Envíanos un Mensaje</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                  style={{ focusRingColor: colors.primary }}
                  placeholder="Tu nombre completo"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                  style={{ focusRingColor: colors.primary }}
                  placeholder="tu@email.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                  style={{ focusRingColor: colors.primary }}
                  placeholder="+56 9 .... ...."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asunto
                </label>
                <select
                  name="asunto"
                  value={formData.asunto}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all"
                  style={{ focusRingColor: colors.primary }}
                >
                  <option value="">Selecciona un asunto</option>
                  <option value="reserva">Pregunta sobre reserva</option>
                  <option value="tarifas">Consulta sobre tarifas</option>
                  <option value="disponibilidad">Disponibilidad de espacios</option>
                  <option value="eventos">Eventos especiales</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensaje *
                </label>
                <textarea
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-all resize-none"
                  style={{ focusRingColor: colors.primary }}
                  placeholder="Cuéntanos tu pregunta o comentario..."
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: colors.primary }}
              >
                {isLoading ? 'Enviando...' : 'Enviar Mensaje'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  )
}

export default ContactPage
